import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Siren, XCircle, CheckCircle2, Phone } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Navbar, goToContact } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SectionHeading, Reveal } from "@/components/Reveal";
import { INCIDENT_STEPS, INCIDENT_DONTS, INCIDENT_PREPARATION, INCIDENT_CONTACTS, PHOTOS } from "@/data/content";

export default function GuidePage() {
  const [open, setOpen] = useState(0);
  const navigate = useNavigate();

  return (
    <div className="bg-[#14163C] min-h-screen" data-testid="guide-page">
      <Seo
        title="Guía Práctica ante Incidentes de Seguridad | Cibermedida"
        description="Qué hacer en las primeras horas de un ciberataque: contención, preservación de evidencias, notificación a la AEPD en 72 horas y recuperación."
      />
      <Navbar />
      <main className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
          <div>
            <SectionHeading
              testId="incident-heading"
              kicker="Guía práctica"
              title="Qué hacer en las primeras horas de un incidente"
              description="Un protocolo claro marca la diferencia entre un susto y una crisis. Estos cuatro pasos ordenan la respuesta de cualquier organización."
            />
            <Reveal delay={0.1}>
              <div className="bg-gradient-to-br from-[#FF4D4D]/10 to-transparent border border-[#FF4D4D]/30 rounded-2xl p-7" data-testid="incident-emergency-card">
                <div className="flex items-center gap-3 mb-3">
                  <Siren size={22} className="text-[#FF6B6B]" />
                  <h2 className="font-heading text-lg font-semibold text-[#EEFBFF]">¿Sospechas de una brecha ahora mismo?</h2>
                </div>
                <p className="text-sm text-[#B9C3EC] leading-relaxed">
                  No apagues los equipos ni borres nada. Cuéntanos qué está ocurriendo y un especialista te indicará los primeros pasos de contención.
                </p>
                <button
                  data-testid="incident-emergency-cta"
                  onClick={() => goToContact(navigate, "He sufrido un ciberataque")}
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#14163C] bg-[#FF6B6B] rounded-full px-6 py-3 hover:bg-[#FF8585] transition-all duration-300"
                >
                  Reportar incidente
                </button>
              </div>
            </Reveal>
          </div>

          <div className="flex flex-col gap-4" data-testid="incident-accordion">
            {INCIDENT_STEPS.map((s, i) => (
              <Reveal key={s.step} delay={i * 0.07}>
                <div
                  className={`border rounded-2xl overflow-hidden transition-colors duration-300 ${
                    open === i ? "bg-[#242764] border-[#3FE0F2]/40" : "bg-[#242764]/50 border-[#3FE0F2]/15 hover:border-[#3FE0F2]/30"
                  }`}
                  data-testid={`incident-step-${i}`}
                >
                  <button
                    data-testid={`incident-step-toggle-${i}`}
                    onClick={() => setOpen(open === i ? -1 : i)}
                    className="w-full flex items-center gap-5 p-6 text-left"
                  >
                    <span className={`font-mono-cyber text-sm font-medium ${open === i ? "text-[#3FE0F2]" : "text-[#7D86C2]"}`}>
                      {s.step}
                    </span>
                    <span className="font-heading flex-1 text-base sm:text-lg font-semibold text-[#EEFBFF]">{s.title}</span>
                    <ChevronDown
                      size={18}
                      className={`text-[#3FE0F2] transition-transform duration-300 ${open === i ? "rotate-180" : ""}`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {open === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <p className="px-6 pb-6 pl-[4.25rem] text-sm text-[#B9C3EC] leading-relaxed" data-testid={`incident-step-body-${i}`}>
                          {s.text}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <section className="mt-28 relative rounded-3xl overflow-hidden border border-[#3FE0F2]/20 min-h-[340px] flex items-end">
            <img src={PHOTOS.servers} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#14163C] via-[#14163C]/85 to-[#14163C]/20" />
            <div className="relative max-w-xl p-8 sm:p-12">
              <span className="font-mono-cyber text-xs uppercase tracking-[0.25em] text-[#FF6B6B]">Errores frecuentes</span>
              <h2 className="font-heading mt-3 text-2xl sm:text-3xl font-semibold text-[#EEFBFF] leading-tight">Lo que nunca debes hacer en caliente</h2>
              <p className="mt-4 text-base text-[#D8E2FF] leading-relaxed">
                Bajo presión es fácil tomar decisiones que destruyen evidencias o agravan el daño. Estas son las más habituales.
              </p>
            </div>
          </section>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {INCIDENT_DONTS.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.06}>
                <div className="h-full bg-[#242764] border border-[#FF4D4D]/25 rounded-2xl p-6">
                  <XCircle size={22} className="text-[#FF6B6B]" />
                  <h3 className="font-heading mt-3 text-base font-semibold text-[#EEFBFF]">{t}</h3>
                  <p className="mt-2 text-sm text-[#B9C3EC] leading-relaxed">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <section className="mt-28 grid lg:grid-cols-5 gap-12 items-start">
            <div className="lg:col-span-2 lg:sticky lg:top-28">
              <SectionHeading
                kicker="Antes del incidente"
                title="La mejor respuesta empieza meses antes"
                description="Las organizaciones que se recuperan en horas y no en semanas tienen algo en común: se prepararon antes de necesitarlo."
              />
              <div className="rounded-2xl overflow-hidden border border-[#3FE0F2]/20">
                <img src={PHOTOS.team} alt="Equipo preparando su plan de respuesta" width="1400" height="933" loading="lazy" className="w-full h-64 object-cover" />
              </div>
            </div>
            <div className="lg:col-span-3 grid sm:grid-cols-2 gap-5">
              {INCIDENT_PREPARATION.map(([t, d], i) => (
                <Reveal key={t} delay={i * 0.05}>
                  <div className="h-full bg-[#242764] border border-[#3FE0F2]/15 rounded-2xl p-6">
                    <CheckCircle2 size={20} className="text-[#06D6A0]" />
                    <h3 className="font-heading mt-3 text-base font-semibold text-[#EEFBFF]">{t}</h3>
                    <p className="mt-2 text-sm text-[#B9C3EC] leading-relaxed">{d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>

          <section className="mt-28">
            <SectionHeading
              kicker="A quién acudir"
              title="Organismos de referencia en España"
              description="Además de tu proveedor de seguridad, estas entidades ofrecen ayuda o reciben las notificaciones obligatorias."
            />
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {INCIDENT_CONTACTS.map(([t, d]) => (
                <div key={t} className="bg-[#1B1E4E] border border-[#3FE0F2]/15 rounded-2xl p-6">
                  <Phone size={18} className="text-[#3FE0F2]" />
                  <h3 className="font-heading mt-3 text-base font-semibold text-[#EEFBFF]">{t}</h3>
                  <p className="mt-2 text-sm text-[#B9C3EC] leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
